import { IToolPanelComp, IToolPanelParams } from 'ag-grid-community';
import { ISimplifiedNode, ISummaryPanelItem, ISummaryPanelItemData } from './criteria-summary.types';

export interface ICustomStatsToolPanelParams extends IToolPanelParams {
    summary: ISummaryPanelItemData[] | undefined;
}

/**
 * Criteria Summary Panel
 * It receives the summary items data, organizes by order and format to display and constructs the
 * HTML
 * Styles are located in guideline.scss
 */
export class CriteriaSummaryPanel implements IToolPanelComp {
    // Returns the DOM element for this Tool Panel
    public eGui!: HTMLDivElement;

    public init(params: ICustomStatsToolPanelParams) {
        this.eGui = document.createElement('div');
        this.eGui.setAttribute('style', 'width: 100%; height: 100%');

        let html = `<div class="summary-container">`;

        if (params.summary?.length) {
            html += `<div class="applied-filters">`;
            html += `<p class="applied-filters-title">Criteria Summary</p>`;

            const summaryItems: ISummaryPanelItem[] = params.summary.map(item => this._buildSummaryItem(item)).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
            summaryItems.forEach(item => {
                html += item.html;
            });

            html += `</div>`; // closes applied-filters
        }

        html += `</div>`; // closes summary-container
        this.eGui.innerHTML = html;
    }

    /**
     * Obligatory class method, returns the panel HTML
     * @returns panel HTML
     */
    public getGui() {
        return this.eGui;
    }

    /**
     * Obligatory class method, can be used in refresh operations
     */
    public refresh(): void {
        // not to do
    }

    /**
     * Escapes HTML characters to prevent XSS
     */
    private _escapeHtml(text: string | number | undefined): string {
        if (text === undefined || text === null) return '';
        return String(text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }

    /**
     * Build the HTML strings according to the display type
     * @param item Data item
     * @returns Item with the HTML string
     */
    private _buildSummaryItem(item: ISummaryPanelItemData): ISummaryPanelItem {
        let html = `<div class="filter-wrap">`;
        html += `<span class="filter-wrap-title">${this._escapeHtml(item.label)}:</span>`;

        switch (item.display) {
            case 'treeNode':
                html += this._renderTreeNodes(item.value as ISimplifiedNode[], 0);
                break;

            case 'numberSelected':
                html += `<span class="filter-wrap-value">${this._escapeHtml(item.value as number)} Selected</span>`;
                break;

            case 'string':
            default:
                html += `<span class="filter-wrap-value">${this._escapeHtml(item.value as string)}</span>`;
                break;
        }

        html += `</div>`;
        return { ...item, html };
    }

    /**
     * Organizes tree nodes with hierarchy and paddings in the HTML
     * @param nodes TreeNodes to organize
     * @param level initial level of the node
     * @returns HTML in string
     */
    private _renderTreeNodes(nodes: ISimplifiedNode[], level: number): string {
        if (!nodes?.length) return '';
        let html = '<ul>';
        nodes.forEach(node => {
            html += `<li style="padding-left:${level * 10}px">${this._escapeHtml(node.label)}`;

            if (this._isLowestLevel(node)) {
                const childLabels = node.children?.map(c => this._escapeHtml(c.label)).join(', ') || '';
                html += `<p style="padding-left:${(level + 1) * 10}px">${childLabels}</p>`;
            } else {
                html += this._renderTreeNodes(node.children ?? [], level + 1);
            }
            html += `</li>`;
        });
        html += '</ul>';
        return html;
    }

    /**
     * Verifies if the node is the lowest level
     * @param node current node
     * @returns true if it is the lowest level
     */
    private _isLowestLevel(node: ISimplifiedNode): boolean {
        return !!node.children && node.children.length > 0 && (!node.children[0].children || node.children[0].children?.length === 0);
    }
}
